import { z as zod } from "zod"
import { addZodError } from "@/src/schemas/formSchemas"
import { AGENTS } from "@/src/assets/data/AgentType"
import {
    isValidGoogleMapsUrl,
    isSchedulerDateRangeValid,
    isSchedulerDailyTimeWindowValid,
    hasDuplicateSchedulerRounds,
    DUPLICATE_SCHEDULER_ROUND_MESSAGE,
} from "@/src/features/jobPosting/jobPostingForm/interviewSetupStep/interviewSetupConstants"

export type FormSchemaJobPostingType = FormSchemaJobDescriptionType &
    FormSchemaJobDetailsType &
    FormSchemaInterviewProcessType &
    FormSchemaRoundsConfigType & {
        status?: string
        agentFriendlyJobDescription?: string
        shouldGenerateRounds?: boolean
    }

export type FormSchemaJobDescriptionType = zod.infer<typeof FormSchemaJobDescription>
export type FormSchemaJobDetailsType = zod.infer<typeof FormSchemaJobDetails>
export type FormSchemaInterviewProcessType = zod.infer<typeof FormSchemaInterviewProcess>
export type FormSchemaRoundsConfigType = zod.infer<typeof FormSchemaRoundsConfig>
export type FormSchemaRoundsConfigRoundType = zod.infer<typeof FormSchemaRoundsConfigRound> & { interviewLink?: string }

// Backwards compatibility aliases
export type FormSchemaMandateType = FormSchemaJobPostingType
export type FormSchemaMandate0Type = FormSchemaJobDescriptionType
export type FormSchemaMandate1Type = FormSchemaJobDetailsType
export type FormSchemaMandate2Type = FormSchemaInterviewProcessType
export type FormSchemaMandate3Type = FormSchemaRoundsConfigType
export type FormSchemaMandate3RoundType = FormSchemaRoundsConfigRoundType

export const FormSchemaJobDescription = zod.object({
    jobDescriptionPrompt: zod.string().min(1, { message: "Job description is required" }),
    jdFile: zod.instanceof(File).nullable(),
    jdFileLink: zod.string().nullable(),
})

export const FormSchemaJobDetails = zod
    .object({
        clientId: zod
            .union([zod.string(), zod.null()])
            .refine((val) => val !== null && val !== "", { message: "Please enter a valid company name" }),
        title: zod.string().min(1, { message: "Job title is required" }),
        jobCity: zod.string().min(1, { message: "City is required" }),
        jobArea: zod.string().min(1, { message: "Area is required" }),
        experienceRequired: zod.string().min(1, { message: "Experience type is required" }),
        experiencedCandidateDefinition: zod.string().optional(),
        fresherCandidateDefinition: zod.string().optional(),
        workType: zod.string().min(1, { message: "Work type is required" }),
        workMode: zod.string().min(1, { message: "Work mode is required" }),
        workScheduleAndShiftDetails: zod.string().min(1, { message: "Work schedule is required" }),
        experiencedCompensationDetails: zod.string().optional(),
        freshersCompensationDetails: zod.string().optional(),
        additionalDetails: zod.string().optional(),
        candidateFaqPairs: zod
            .array(zod.object({ question: zod.string(), answer: zod.string() }))
            .optional()
            .default([]),
        candidateFaqFileName: zod.string().optional(),
        candidateFaqFileUrl: zod.string().optional(),
    })
    .superRefine((data, ctx) => {
        const experiencedDefinition = data.experiencedCandidateDefinition?.trim()
        const fresherDefinition = data.fresherCandidateDefinition?.trim()
        const experiencedCompensationDetails = data.experiencedCompensationDetails?.trim()
        const freshersCompensationDetails = data.freshersCompensationDetails?.trim()

        if (data.experienceRequired === "EXPERIENCED_ONLY") {
            if (!experiencedDefinition?.length) {
                addZodError(ctx, "experiencedCandidateDefinition", "Experienced candidate definition is required")
            }
            if (!experiencedCompensationDetails?.length) {
                addZodError(ctx, "experiencedCompensationDetails", "Experienced compensation details are required")
            }
        } else if (data.experienceRequired === "FRESHER_ONLY") {
            if (!fresherDefinition?.length) {
                addZodError(ctx, "fresherCandidateDefinition", "Fresher candidate definition is required")
            }
            if (!freshersCompensationDetails?.length) {
                addZodError(ctx, "freshersCompensationDetails", "Fresher compensation details are required")
            }
        } else {
            if (!experiencedCompensationDetails?.length) {
                addZodError(ctx, "experiencedCompensationDetails", "Experienced compensation details are required")
            }
            if (!freshersCompensationDetails?.length) {
                addZodError(ctx, "freshersCompensationDetails", "Fresher compensation details are required")
            }
            if (!experiencedDefinition?.length) {
                addZodError(ctx, "experiencedCandidateDefinition", "Experienced candidate definition is required")
            }
            if (!fresherDefinition?.length) {
                addZodError(ctx, "fresherCandidateDefinition", "Fresher candidate definition is required")
            }
        }
    })

export const FormSchemaInterviewProcess = zod.object({
    interviewProcess: zod.string().min(1, { message: "Interview process description is required" }),
})

const agentTypeValues = Object.values(AGENTS).map((agent) => agent.value) as [string, ...string[]]

export const FormSchemaRoundFormSelection = zod.object({
    agentType: zod.enum(agentTypeValues, { message: "Please select a agent type" }),
    roundName: zod.string().min(1, { message: "Round name is required" }),
    roundDescription: zod.string().optional(),
})

export type FormSchemaRoundFormSelectionType = zod.infer<typeof FormSchemaRoundFormSelection>

export const FormSchemaRoundsConfigRound = zod.object({
    id: zod.string().optional().nullable(),
    jobId: zod.string().optional().nullable(),
    roundNumber: zod.number().min(1, { message: "Round number is required" }),
    maxNoOfCriteria: zod.number().optional().nullable(),
    agentType: zod.enum(agentTypeValues, { message: "Please select a valid agent type" }).nullable(),
    roundType: zod.string().optional().default("QUICK_SCREENING"),
    roundName: zod.string().min(1, { message: "Round name is required" }),
    roundBriefDescription: zod.string().optional(),
    roundDuration: zod.number().min(1, { message: "Round duration is required" }),
    roundMetadataId: zod.string().optional().nullable(),
    roundCost: zod.number().default(1),
    aiEnabled: zod.boolean(),
    aiInterviewFormat: zod.enum(["CHAT", "WEB", "VIDEO"]).optional(),
    aiInterviewLanguage: zod.enum(["ENGLISH", "HINDI"]).optional(),
    aiRecruiterId: zod.string().optional(),
    fresherCriteria: zod.array(
        zod.object({
            text: zod.string().min(1, { message: "Criteria is required" }),
            type: zod.enum(["must_have", "preferred", "red_flag"]),
        })
    ),
    experiencedCriteria: zod.array(
        zod.object({
            text: zod.string().min(1, { message: "Criteria is required" }),
            type: zod.enum(["must_have", "preferred", "red_flag"]),
        })
    ),

    // Round scheduling
    moveToNextRoundAutomatically: zod.boolean(),
    scheduleNextRoundDuringCurrentCall: zod.boolean(),
    nonAiRoundDetails: zod.object({
        roundMode: zod.enum(["FACE_TO_FACE", "ONLINE"]).nullable(),
        interviewLink: zod.string().nullable(),
        interviewDateTime: zod.string().nullable(),
        interviewAddress: zod.string().nullable(),
        interviewMapLink: zod.string().nullable(),
        interviewInstruction: zod.string().nullable(),
    }),

    // Round plan add-on
    roundPlanAddOn: zod.object({
        proctoringEnabled: zod.boolean().optional(),
        storeInterviewRecording: zod.boolean().optional(),
        advancedEvaluationView: zod.boolean().optional(),
        outreachEnabled: zod.boolean().optional(),
        outreachEnableType: zod.enum(["PRO", "ADVANCED"]).optional(),
        interviewReminderEnabled: zod.boolean().optional(),
        interviewReminderType: zod.enum(["PRO", "ADVANCED"]).optional(),
        directionalCefr: zod.boolean().optional(),
        intentChecker: zod.boolean().optional(),
        interviewSchedulingEnabled: zod.boolean().optional(),
    }),
    taskType: zod.enum(["SCREENING", "INTERVIEW", "INTERVIEW_SCHEDULING"]).optional(),
    isSingleCallSchedulerRound: zod.boolean().optional(),
    sourceRoundId: zod.string().optional().nullable(),
    isInboundEnabled: zod.boolean().optional(),
    isOutboundEnabled: zod.boolean().optional(),
    assessmentConfig: zod
        .object({
            enabled: zod.boolean().optional().nullable(),
            questionBanks: zod.array(
                zod.object({
                    questionBankId: zod.string(),
                    noOfQuestionsToAsk: zod.number().min(1),
                    name: zod.string().optional().nullable(),
                    questionCount: zod.number().optional().nullable(),
                })
            ),
        })
        .optional()
        .nullable(),
    schedulerDetails: zod
        .object({
            interviewStartDate: zod.string().optional().nullable(),
            interviewEndDate: zod.string().optional().nullable(),
            dailyStartTime: zod.string().optional().nullable(),
            dailyEndTime: zod.string().optional().nullable(),
            interviewAddress: zod.string().optional().nullable(),
            mapsUrl: zod
                .string()
                .nullable()
                .refine((mapsUrl) => isValidGoogleMapsUrl(mapsUrl), {
                    message: "Enter a valid Google Maps URL",
                }),
            hrName: zod.string().optional().nullable(),
            hrPhoneNumber: zod
                .string()
                .optional()
                .nullable()
                .refine((phoneNumber) => !phoneNumber || /^\d{10}$/.test(phoneNumber), {
                    message: "HR number should be 10 digits",
                }),
            interviewDays: zod.array(zod.string()).optional(),
        })
        .optional(),
    cefrConfig: zod.object({
        minimumLevel: zod
            .enum([
                "A1_BEGINNER",
                "A2_ELEMENTARY",
                "B1_INTERMEDIATE",
                "B2_UPPER_INTERMEDIATE",
                "C1_ADVANCED",
                "C2_PROFICIENT",
            ])
            .optional()
            .nullable(),
        preferredLevel: zod
            .enum([
                "A1_BEGINNER",
                "A2_ELEMENTARY",
                "B1_INTERMEDIATE",
                "B2_UPPER_INTERMEDIATE",
                "C1_ADVANCED",
                "C2_PROFICIENT",
            ])
            .optional()
            .nullable(),
        assessmentQuestions: zod.string().optional().nullable(),
    }),
    interviewFormat: zod.string().optional(),
})

export const FormSchemaRoundsConfig = zod.object({
    completeRoundsInSingleCall: zod.boolean().optional().default(false),
    rounds: zod
        .array(FormSchemaRoundsConfigRound)
        .min(1, { message: "Rounds are required" })
        .superRefine((data, ctx) => {
            if (hasDuplicateSchedulerRounds(data)) {
                addZodError(ctx, "rounds", DUPLICATE_SCHEDULER_ROUND_MESSAGE)
            }

            data.forEach((round, roundIndex) => {
                if (round.aiEnabled) {
                    if (!round.aiInterviewFormat) {
                        addZodError(
                            ctx,
                            `rounds.${round.roundNumber}.aiInterviewFormat`,
                            "AI interview format is required"
                        )
                    }
                    if (!round.aiInterviewLanguage) {
                        addZodError(
                            ctx,
                            `rounds.${round.roundNumber}.aiInterviewLanguage`,
                            "AI interview language is required"
                        )
                    }
                    if (!round.aiRecruiterId) {
                        addZodError(ctx, `rounds.${round.roundNumber}.aiRecruiterId`, "AI recruiter ID is required")
                    }
                }

                if (round.taskType === "INTERVIEW_SCHEDULING" && round.schedulerDetails) {
                    const schedulerDetails = round.schedulerDetails

                    if (
                        schedulerDetails.interviewStartDate?.trim() &&
                        schedulerDetails.interviewEndDate?.trim() &&
                        !isSchedulerDateRangeValid(
                            schedulerDetails.interviewStartDate,
                            schedulerDetails.interviewEndDate
                        )
                    ) {
                        addZodError(
                            ctx,
                            `rounds.${roundIndex}.schedulerDetails.interviewEndDate`,
                            "End date cannot be earlier than start date"
                        )
                    }

                    if (
                        schedulerDetails.dailyStartTime?.trim() &&
                        schedulerDetails.dailyEndTime?.trim() &&
                        !isSchedulerDailyTimeWindowValid(
                            schedulerDetails.dailyStartTime,
                            schedulerDetails.dailyEndTime
                        )
                    ) {
                        addZodError(
                            ctx,
                            `rounds.${roundIndex}.schedulerDetails.dailyEndTime`,
                            "End time must be later than start time"
                        )
                    }
                }
            })
        }),
})

// Backwards compatibility aliases for schema names
export const FormSchemaMandate0 = FormSchemaJobDescription
export const FormSchemaMandate1 = FormSchemaJobDetails
export const FormSchemaMandate2 = FormSchemaInterviewProcess
export const FormSchemaMandate3 = FormSchemaRoundsConfig
export const FormSchemaMandate3Round = FormSchemaRoundsConfigRound
